// ─── Distributed Order Lock ─────────────────────────────────
// A lease-based distributed lock backed by Supabase, used to
// guarantee that only ONE device places a BUY/SELL order for a
// given (strategy, account, symbol) at any moment.
//
// The lock is stored in the `chin_order_locks` table and mutated
// through two atomic RPC functions (`acquire_order_lock` and
// `release_order_lock`). Acquire uses INSERT ... ON CONFLICT DO
// UPDATE ... WHERE expires_at < now(), so:
//   - a brand-new key is claimed immediately
//   - an expired lock (owner crashed / forgot to release) can be
//     taken over by anyone
//   - a live lock returns the CURRENT owner (so the caller knows
//     it did NOT acquire it)
//
// The acquire/release calls are deliberately fail-open: if the
// lock RPC itself errors (transient network/db issue), we treat it
// as acquired so a legit order is never dropped. This is safe
// because the actual place-order call goes through the same
// Supabase gateway — if that's down, no order is placed anyway.

import { supabase } from './supabase';
import { getDeviceWriterId } from './device-id';

const DEFAULT_TTL_SECONDS = 20;

export interface OrderLockAcquireResult {
  /** true if THIS device now holds the lock (or fail-open fallback). */
  acquired: boolean;
  /** The owner token used for this attempt — pass back to release. */
  ownerId: string;
}

export async function acquireOrderLock(
  lockKey: string,
  ttlSeconds: number = DEFAULT_TTL_SECONDS,
): Promise<OrderLockAcquireResult> {
  const ownerId = getDeviceWriterId();

  try {
    const { data, error } = await supabase.rpc('acquire_order_lock', {
      p_lock_key: lockKey,
      p_owner_id: ownerId,
      p_ttl_seconds: ttlSeconds,
    });

    if (error) {
      console.warn('[OrderLock] acquire RPC error, failing open:', error.message);
      return { acquired: true, ownerId };
    }

    const currentOwner = typeof data === 'string' ? data : null;
    if (currentOwner === ownerId) {
      return { acquired: true, ownerId };
    }

    return { acquired: false, ownerId };
  } catch (err) {
    console.warn('[OrderLock] acquire threw, failing open:', err);
    return { acquired: true, ownerId };
  }
}

export async function releaseOrderLock(lockKey: string, ownerId: string): Promise<void> {
  try {
    const { error } = await supabase.rpc('release_order_lock', {
      p_lock_key: lockKey,
      p_owner_id: ownerId,
    });
    if (error) {
      console.warn('[OrderLock] release RPC error:', error.message);
    }
  } catch (err) {
    // Best-effort — the lock will expire on its own via TTL.
    console.warn('[OrderLock] release threw:', err);
  }
}